import { CHANGE_TYPE_ICONS } from "./SaveDropdown";
import { PushPin as PushPinIcon, Undo as UndoIcon } from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import * as labels from "config.labels";
import React from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const ColumnList = ({
  title,
  items,
  pinnedSet,
  hiddenSet,
  isSavedColumn = false,
}) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box
      sx={{
        position: "sticky",
        top: 0,
        backgroundColor: "background.paper",
        zIndex: 1,
        pb: 1,
      }}
    >
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        fontWeight={600}
        sx={{ flex: 1 }}
      >
        {title}
      </Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        backgroundColor: "grey.100",
        p: 1,
        borderRadius: 1,
        overflowY: "auto",
      }}
    >
      {items.map((item, idx) => {
        const isHidden = hiddenSet?.has(item.col);
        const color = isHidden
          ? "grey.500"
          : isSavedColumn
          ? item.removed
            ? "error.dark"
            : item.moved
            ? "warning.dark"
            : "success.dark"
          : item.added
          ? "success.dark"
          : item.moved
          ? "warning.dark"
          : "warning.dark";

        const fontWeight = isHidden
          ? 400
          : item.removed || item.moved || item.added
          ? 600
          : 400;

        return (
          <Box
            key={`${isSavedColumn ? "saved" : "current"}-${item.col}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
              fontFamily: "monospace",
              fontSize: "0.75rem",
              color,
              fontWeight,
              fontStyle: isHidden ? "italic" : "normal",
            }}
          >
            <Typography
              component={"span"}
              sx={{
                fontSize: "0.65rem",
                color: "text.disabled",
                minWidth: "2ch",
                textAlign: "right",
              }}
            >
              {idx + 1}
            </Typography>
            {pinnedSet.has(item.col) && (
              <PushPinIcon
                sx={{
                  fontSize: "0.75rem",
                  color: isHidden ? "grey.400" : "text.secondary",
                }}
              />
            )}
            <Typography
              component={"span"}
              sx={{
                fontSize: "0.75rem",
                fontFamily: "monospace",
              }}
            >
              {item.col}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const StateDisplay = ({ title, lines, isPending = false }) => (
  <>
    {title && (
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        fontWeight={600}
        sx={{ display: "block", mb: 0.5 }}
      >
        {title}
      </Typography>
    )}
    <Box
      sx={{
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
        maxHeight: 200,
        overflowY: "auto",
      }}
    >
      {lines.map((line, idx) => (
        <Typography
          key={idx}
          sx={{
            fontFamily: "monospace",
            fontSize: "0.7rem",
            color: isPending
              ? "warning.dark"
              : idx === 0
              ? "success.dark"
              : "text.secondary",
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  </>
);

const ChangeComparison = ({ change, details }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const isColumns = change.type === "columns";

  if (isColumns && details.columnOrderDiff) {
    const savedPinnedSet = new Set(
      change.columnOrderInfo?.savedPinnedColumns ?? []
    );
    const currentPinnedSet = new Set(
      change.columnOrderInfo?.pinnedColumns ?? []
    );

    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          maxHeight: 400,
          overflow: "auto",
          mb: 2,
        }}
      >
        <ColumnList
          title={translateLabels(labels.CHANGE_DETAILS_SAVED, {
            default: "Saved",
          })}
          items={details.columnOrderDiff.saved}
          pinnedSet={savedPinnedSet}
          hiddenSet={details.savedHiddenSet}
          isSavedColumn
        />
        <ColumnList
          title={translateLabels(labels.CHANGE_DETAILS_CURRENT, {
            default: "Current",
          })}
          items={details.columnOrderDiff.current}
          pinnedSet={currentPinnedSet}
          hiddenSet={details.currentHiddenSet}
          isSavedColumn={false}
        />
      </Box>
    );
  }

  if (details.widthChanges) {
    return (
      <Box sx={{ mb: 2 }}>
        <StateDisplay lines={details.widthChanges} isPending />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <StateDisplay
        title={translateLabels(labels.CHANGE_DETAILS_SAVED, {
          default: "Saved",
        })}
        lines={details.savedState}
      />
      <StateDisplay
        title={translateLabels(labels.CHANGE_DETAILS_CURRENT, {
          default: "Current",
        })}
        lines={details.currentState}
        isPending
      />
    </Box>
  );
};

const ChangeHeader = ({ change }) => {
  const Icon = CHANGE_TYPE_ICONS[change.type];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Box sx={{ color: "primary.main" }}>
        <Icon fontSize={"small"} />
      </Box>
      <Typography variant={"subtitle1"} fontWeight={600}>
        {change.label}
      </Typography>
    </Box>
  );
};

const ActionButtons = ({ change, onRevertChange, onClose }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
      <MuiButton
        variant={"outlined"}
        size={"small"}
        startIcon={<UndoIcon />}
        onClick={() => {
          onRevertChange(change);
          onClose();
        }}
        sx={{
          borderColor: "warning.main",
          color: "warning.dark",
        }}
      >
        {translateLabels(labels.CHANGE_DETAILS_REVERT, {
          default: "Revert",
        })}
      </MuiButton>
    </Box>
  );
};

export const ChangeDetailPopover = ({
  open,
  anchorEl,
  change,
  fullscreenContainerRef,
  getChangeDetails,
  onClose,
  onRevertChange,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      container={fullscreenContainerRef.current}
      sx={{ zIndex: 1400 }}
    >
      {change && (
        <Paper sx={{ minWidth: 300, maxWidth: 450, p: 2 }}>
          <ChangeHeader change={change} />
          <ChangeComparison
            change={change}
            details={getChangeDetails(change)}
          />
          <ActionButtons
            change={change}
            onRevertChange={onRevertChange}
            onClose={onClose}
          />
        </Paper>
      )}
    </Popover>
  );
};
